/**
 * Program 12.1 UX4 — AI Bid Brief panel (deterministic + advisory questions).
 */
import React, { useMemo } from 'react';
import { Card, List, Space, Tag, Typography } from 'antd';
import type { BidWorkspaceViewModel } from './bidWorkspaceModel';
import { buildBidBriefQuestions, buildBidBriefSignals } from './earlyBidBrief';

const { Text } = Typography;

const kindColor: Record<string, string> = {
  FACT: 'blue',
  CALCULATION: 'green',
  MISSING: 'orange',
  INFERENCE: 'purple',
};

export type EpcBidBriefPanelProps = {
  vm: BidWorkspaceViewModel;
  proposal?: Record<string, unknown> | null;
};

export const EpcBidBriefPanel: React.FC<EpcBidBriefPanelProps> = ({ vm, proposal }) => {
  const signals = useMemo(() => buildBidBriefSignals({ vm, proposal }), [vm, proposal]);
  const questions = useMemo(() => buildBidBriefQuestions(signals), [signals]);

  return (
    <Card size="small" title="Bid brief (advisory)">
      <Text strong>What matters now?</Text>
      <List
        size="small"
        dataSource={signals.slice(0, 8)}
        renderItem={(s) => (
          <List.Item>
            <Tag color={kindColor[s.kind] || 'default'}>{s.kind}</Tag>
            <Text>{s.label}</Text>
            {s.detail && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {s.detail}
              </Text>
            )}
          </List.Item>
        )}
      />
      <Space direction="vertical" style={{ marginTop: 12, width: '100%' }}>
        <Text strong>Questions before approval</Text>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {questions.map((q) => (
            <li key={q}>
              <Text type="secondary">{q}</Text>
            </li>
          ))}
        </ul>
      </Space>
    </Card>
  );
};

export default EpcBidBriefPanel;
